import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Lock, Unlock, Euro, Copy, RefreshCw, UserPlus, Info, Send, BedDouble, Star, MessageSquare } from "lucide-react";
import { roomHeadlinePrice } from "@/lib/pricing";
import GuestReviewDialog from "@/components/GuestReviewDialog";

// ---------- helpers ----------
const daysInMonth = (year, month /* 0-indexed */) => {
  const last = new Date(year, month + 1, 0).getDate();
  const arr = [];
  for (let d = 1; d <= last; d++) {
    const dt = new Date(year, month, d);
    arr.push({
      day: d,
      iso: dt.toISOString().slice(0, 10),
      dow: (dt.getDay() + 6) % 7, // 0=Mon, 6=Sun
    });
  }
  return arr;
};
const monthLabel = (year, month, lang) => {
  const d = new Date(year, month, 1);
  return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { month: "long", year: "numeric" });
};
const CAPACITY_LABELS = {
  1: { it: "Singola", en: "Single" },
  2: { it: "Doppia", en: "Double" },
  3: { it: "Tripla", en: "Triple" },
  4: { it: "Quadrupla", en: "Quadruple" },
  5: { it: "Quintupla", en: "Quintuple" },
  6: { it: "Sestupla", en: "Sextuple" },
};
const capacityLabel = (cap, IT) => {
  const c = parseInt(cap, 10) || 2;
  const preset = CAPACITY_LABELS[c];
  if (preset) return IT ? preset.it : preset.en;
  return (IT ? `${c} posti letto` : `${c} beds`);
};
const capacityTint = (cap) => {
  const c = parseInt(cap, 10) || 2;
  if (c >= 4) return "bg-primary/15 text-primary border-primary/30";
  if (c === 3) return "bg-sage/15 text-sage border-sage/30";
  if (c === 2) return "bg-sand text-foreground border-border";
  return "bg-muted/50 text-muted-foreground border-border";
};
const groupByCapacity = (rooms) => {
  const byCap = new Map();
  (rooms || []).forEach((r) => {
    const c = parseInt(r.capacity, 10) || 2;
    if (!byCap.has(c)) byCap.set(c, []);
    byCap.get(c).push(r);
  });
  return Array.from(byCap.entries())
    .sort(([a], [b]) => a - b)
    .map(([cap, list]) => ({ cap, rooms: list }));
};
const statusCellClass = (status) => {
  switch (status) {
    case "direct": return "bg-primary/85 text-primary-foreground";
    case "ota": return "bg-destructive/80 text-white";
    case "blocked": return "bg-muted-foreground/30 text-foreground";
    default: return "bg-card hover:bg-primary/5";
  }
};

export default function CalendarTab({ rooms }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const today = useMemo(() => new Date(), []);
  const todayIso = useMemo(() => today.toISOString().slice(0, 10), [today]);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [roomData, setRoomData] = useState({}); // { room_id: {room, days} }
  const [feedUrls, setFeedUrls] = useState({}); // { room_id: {url, token} }
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { roomId, date }
  const [manualOpen, setManualOpen] = useState(false);
  const [prefill, setPrefill] = useState({ roomId: "", checkIn: "" });
  const [channels, setChannels] = useState([]);
  const [pushing, setPushing] = useState(null);
  const [dragged, setDragged] = useState(null);  // { id, kind, from_room, check_in, check_out, guest_name }
  const [dragTarget, setDragTarget] = useState(null);  // room_id being hovered
  const [openedBookingId, setOpenedBookingId] = useState(null); // booking detail dialog
  const [openedIcalId, setOpenedIcalId] = useState(null);       // ical event detail dialog
  const [bulkForRoom, setBulkForRoom] = useState(null);         // room object → open bulk-edit dialog
  const [presetForRoom, setPresetForRoom] = useState(null);     // room object → open preset picker
  const [managePresetsOpen, setManagePresetsOpen] = useState(false);
  const [presets, setPresets] = useState([]);

  const loadPresets = async () => {
    try {
      const r = await api.get("/season-presets");
      setPresets(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPresets(); }, []);
  // Calendar view mode:
  //  - "month": traditional full-month grid (prev/next arrows navigate months)
  //  - "today": rolling 32-day window starting yesterday so today lands as the 2nd column
  // Default is "today" so the manager opens the calendar right on the current
  // date instead of the 1st of the month — matches how host apps behave in
  // production and skips one click for the most common flow.
  const [viewMode, setViewMode] = useState("today");
  const [todayAnchor, setTodayAnchor] = useState(() => {
    const y = new Date(); y.setDate(y.getDate() - 1); return y;
  });

  const days = useMemo(() => {
    if (viewMode === "today" && todayAnchor) {
      const out = [];
      const ITWD = ["DO","LU","MA","ME","GI","VE","SA"];
      for (let i = 0; i < 32; i++) {
        const dt = new Date(todayAnchor);
        dt.setDate(dt.getDate() + i);
        const iso = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
        out.push({ iso, day: dt.getDate(), wd: ITWD[dt.getDay()] });
      }
      return out;
    }
    return daysInMonth(year, month);
  }, [viewMode, todayAnchor, year, month]);

  const monthRange = useMemo(() => {
    if (viewMode === "today" && todayAnchor) {
      const from = days[0].iso;
      const lastIso = days[days.length - 1].iso;
      const end = new Date(lastIso); end.setDate(end.getDate() + 1);
      const to = `${end.getFullYear()}-${String(end.getMonth()+1).padStart(2,"0")}-${String(end.getDate()).padStart(2,"0")}`;
      return { from, to };
    }
    const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const toDate = new Date(year, month + 1, 1);
    const to = toDate.toISOString().slice(0, 10);
    return { from, to };
  }, [viewMode, todayAnchor, days, year, month]);

  // Load calendar for ALL rooms in parallel
  const load = useCallback(async () => {
    if (!rooms?.length) return;
    setLoading(true);
    try {
      const results = await Promise.all(rooms.map((r) =>
        api.get(`/calendar/room/${r.id}`, { params: monthRange }).then((res) => ({ id: r.id, data: res.data }))
      ));
      const map = {};
      results.forEach(({ id, data }) => {
        const byIso = {};
        data.days.forEach((d) => { byIso[d.date] = d; });
        map[id] = { room: data.room, byIso };
      });
      setRoomData(map);
    } catch (e) {
      console.error("multi-room calendar load failed", e);
      toast.error(errMsg(e, IT ? "Errore caricamento" : "Load failed"));
    } finally { setLoading(false); }
  }, [rooms, monthRange, IT]);
  useEffect(() => { load(); }, [load]);

  const moveBooking = useCallback(async (item, targetRoomId) => {
    // Dispatch to the right endpoint based on whether the dragged cell is a
    // direct booking (POST /bookings/{id}/move) or an OTA iCal event
    // (POST /ical-events/{id}/move — added in Iteration 57). Both endpoints
    // share the same conflict rules; we just need to hit the right one.
    try {
      const url = item.kind === "ical"
        ? `/ical-events/${item.id}/move`
        : `/bookings/${item.id}/move`;
      await api.post(url, { room_id: targetRoomId });
      toast.success(IT ? "Prenotazione spostata" : "Booking moved");
      load();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Impossibile spostare" : "Cannot move"));
    }
  }, [IT, load]);

  const swapBookings = useCallback(async (a, b) => {
    // Two bookings/OTA events on different rooms — try to swap their room
    // assignments atomically. Backend enforces that the swap only succeeds if
    // BOTH new positions are free of OTHER conflicts.
    try {
      await api.post("/bookings/swap", {
        a: { id: a.id, kind: a.kind },
        b: { id: b.id, kind: b.kind },
      });
      toast.success(IT ? "Camere scambiate" : "Rooms swapped");
      load();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Scambio impossibile" : "Swap failed"));
    }
  }, [IT, load]);

  // Load feed URLs for all rooms
  const loadFeeds = useCallback(async () => {
    if (!rooms?.length) return;
    try {
      const results = await Promise.all(rooms.map((r) =>
        api.get(`/rooms/${r.id}/ical-feed-url`).then((res) => ({ id: r.id, data: res.data }))
      ));
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const map = {};
      results.forEach(({ id, data }) => {
        map[id] = { url: `${base}${data.path}`, token: data.token };
      });
      setFeedUrls(map);
    } catch (e) { console.error("feed urls load failed", e); }
  }, [rooms]);
  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  // Channels
  useEffect(() => {
    api.get("/channels/me").then((r) => setChannels(r.data || [])).catch(() => {});
  }, []);

  const prevMonth = () => {
    setViewMode("month");
    setTodayAnchor(null);
    if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    setViewMode("month");
    setTodayAnchor(null);
    if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1);
  };
  const goToday = () => {
    // Rolling 32-day window starting from yesterday so today sits in the 2nd column
    const y = new Date(today); y.setDate(y.getDate() - 1);
    setTodayAnchor(y);
    setViewMode("today");
    // Also sync month/year to today's month (used by other UI reading them, e.g. push panel)
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  // Header label: month name for "month" mode, "18 Ago → 17 Set 2026" range for "today" mode
  const headerLabel = useMemo(() => {
    if (viewMode === "today" && days.length) {
      const first = new Date(days[0].iso);
      const last = new Date(days[days.length - 1].iso);
      const monthsIT = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
      const monthsEN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const M = IT ? monthsIT : monthsEN;
      const sameYear = first.getFullYear() === last.getFullYear();
      const left = `${first.getDate()} ${M[first.getMonth()]}${sameYear ? "" : " " + first.getFullYear()}`;
      const right = `${last.getDate()} ${M[last.getMonth()]} ${last.getFullYear()}`;
      return `${left} → ${right}`;
    }
    return null; // fall back to existing month/year display
  }, [viewMode, days, IT]);

  const openManual = (roomId, iso) => {
    setPrefill({ roomId: roomId || rooms[0]?.id || "", checkIn: iso || "" });
    setManualOpen(true);
  };
  const copyFeed = async (url) => {
    try { await navigator.clipboard.writeText(url);
      toast.success(IT ? "URL copiato" : "URL copied");
    } catch { toast.error("Clipboard error"); }
  };
  const rotateFeed = async (roomId) => {
    if (!confirm(IT ? "Rigenerare il token invaliderà l'URL corrente. Procedere?" : "Regenerating will invalidate the current URL. Continue?")) return;
    try { await api.post(`/rooms/${roomId}/ical-feed-rotate`); await loadFeeds();
      toast.success(IT ? "Token rigenerato" : "Token rotated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pushRates = async (channelId, providerName) => {
    setPushing(channelId);
    try {
      const r = await api.post(`/channels/${channelId}/push-rates`, {
        from_date: monthRange.from, to_date: monthRange.to, include_defaults: true,
      });
      const sb = r.data.sandbox ? " (sandbox)" : "";
      toast.success(IT
        ? `${providerName}: ${r.data.pushed} tariffe inviate${sb}`
        : `${providerName}: ${r.data.pushed} rates pushed${sb}`);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Push fallito" : "Push failed"));
    } finally { setPushing(null); }
  };

  const groups = useMemo(() => groupByCapacity(rooms || []), [rooms]);

  return (
    <div className="space-y-6" data-testid="calendar-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={prevMonth} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4"/></Button>
          <div className="font-display text-2xl min-w-[240px] text-center capitalize" data-testid="calendar-month-label">
            {headerLabel || monthLabel(year, month, lang)}
          </div>
          <Button size="sm" variant="outline" onClick={nextMonth} data-testid="calendar-next"><ChevronRight className="w-4 h-4"/></Button>
          <Button size="sm" variant="ghost" onClick={goToday} className="text-xs ml-1" data-testid="calendar-today">{IT ? "Oggi" : "Today"}</Button>
          {loading && <span className="text-primary text-xs animate-pulse ml-2">{IT ? "Aggiornamento…" : "Loading…"}</span>}
        </div>
        <Button size="sm" onClick={() => openManual("", "")} className="bg-sage text-white hover:bg-sage/90 lift" data-testid="calendar-manual-btn">
          <UserPlus className="w-4 h-4 mr-1"/>{IT ? "Prenotazione manuale" : "Manual booking"}
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-card border border-border"/>{IT ? "Libera" : "Free"}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary/85"/>{IT ? "Diretta" : "Direct"}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-destructive/80"/>OTA</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-muted-foreground/30"/>{IT ? "Bloccata" : "Blocked"}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-current"/>{IT ? "Prezzo custom" : "Custom price"}</span>
      </div>

      {/* Multi-room grid — one row per room */}
      <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="calendar-multi-grid">
        <div className="px-3 py-2 bg-sand/40 border-b border-border/60 text-[10px] text-muted-foreground flex items-center gap-1.5" data-testid="dnd-hint">
          <span className="uppercase tracking-widest font-semibold text-primary">{IT ? "Suggerimento" : "Tip"}</span>
          {IT
            ? <>Trascina una prenotazione <strong className="text-foreground">D</strong> (diretta) da una camera all'altra per spostarla — le camere non compatibili verranno rifiutate automaticamente.</>
            : <>Drag a <strong className="text-foreground">D</strong> (direct) booking from one room to another to move it — incompatible rooms are rejected automatically.</>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-separate border-spacing-0 min-w-max">
            <thead>
              <tr className="bg-muted/40">
                <th className="sticky left-0 z-10 bg-muted/40 text-left px-3 py-2 uppercase tracking-widest text-[10px] text-muted-foreground border-b border-border min-w-[180px]">
                  {IT ? "Camera" : "Room"}
                </th>
                {days.map((d) => {
                  const isToday = d.iso === todayIso;
                  const isWeekend = d.dow >= 5;
                  return (
                    <th key={d.iso}
                      className={`px-1 py-2 text-center font-medium border-b border-border min-w-[44px] ${
                        isWeekend ? "bg-sand/40" : ""
                      } ${isToday ? "text-primary" : "text-muted-foreground"}`}
                      data-testid={`col-header-${d.iso}`}
                    >
                      <div className={`text-[9px] uppercase tracking-widest ${isToday ? "text-primary" : "opacity-70"}`}>
                        {new Date(d.iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short" }).slice(0, 2)}
                      </div>
                      <div className={`text-sm ${isToday ? "font-bold" : ""}`}>{d.day}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {groups.map((grp) => (
                <React.Fragment key={`cap-${grp.cap}`}>
                  <tr data-testid={`capacity-section-${grp.cap}`}>
                    <td colSpan={days.length + 1} className="sticky left-0 bg-muted/30 border-b border-border px-3 py-1.5">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${capacityTint(grp.cap)}`}>
                        <BedDouble className="w-3 h-3"/>
                        {capacityLabel(grp.cap, IT)}
                        <span className="opacity-60 normal-case tracking-normal font-medium">· {grp.rooms.length} {grp.rooms.length === 1 ? (IT ? "camera" : "room") : (IT ? "camere" : "rooms")}</span>
                      </span>
                    </td>
                  </tr>
                  {grp.rooms.map((r) => {
                    const rd = roomData[r.id];
                    const roomForPrice = rd?.room || r;
                    const hp = roomHeadlinePrice(roomForPrice);
                    const defaultPrice = hp.price;
                    return (
                      <tr key={r.id} data-testid={`room-row-${r.id}`}>
                        <td className="sticky left-0 z-10 bg-card border-r border-border px-3 py-2 min-w-[180px]">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <BedDouble className="w-4 h-4 text-primary shrink-0 mt-0.5"/>
                              <div className="min-w-0">
                                <div className="font-medium text-sm leading-tight truncate">{r.name}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`room-headline-price-${r.id}`}>
                                  {hp.fromRange && <span>{IT ? "da " : "from "}</span>}€{Math.round(defaultPrice)}/{IT ? "notte" : "night"}
                                  {hp.hasTiers && <span className="text-sage ml-1">· occ.</span>}
                                  <span> · {r.capacity || 2} {IT ? "posti" : "beds"}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => setBulkForRoom(r)}
                                className="text-[10px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors whitespace-nowrap"
                                data-testid={`bulk-rates-btn-${r.id}`}
                                title={IT ? "Modifica in blocco le tariffe di questa camera" : "Bulk edit rates for this room"}
                              >
                                €&nbsp;{IT ? "in blocco" : "bulk"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setPresetForRoom(r)}
                                className="text-[10px] px-2 py-1 rounded-full border border-sage/40 text-sage hover:bg-sage hover:text-white transition-colors whitespace-nowrap"
                                data-testid={`preset-btn-${r.id}`}
                                title={IT ? "Applica un preset stagionale" : "Apply a season preset"}
                              >
                                {IT ? "Preset" : "Preset"}{presets.length ? ` (${presets.length})` : ""}
                              </button>
                            </div>
                          </div>
                        </td>
                        {days.map((d) => {
                          const cell = rd?.byIso?.[d.iso];
                          const status = cell?.status || "free";
                          const price = cell?.price ?? defaultPrice;
                          const override = cell?.has_override;
                          const isToday = d.iso === todayIso;
                          const isDraggable = (status === "direct" && !!cell?.booking_id)
                            || (status === "ota" && !!cell?.ical_event_id);
                          // First night of a stay — used to render the guest name label.
                          // For direct bookings we use cell.check_in; for OTA (iCal) events cell.ev_start.
                          const isFirstNight =
                            (status === "direct" && cell?.check_in === d.iso) ||
                            (status === "ota" && cell?.ev_start === d.iso);
                          // Cell we could drop onto:
                          //   - a FREE, non-blocked cell in another room (move)
                          //   - an OCCUPIED cell in another room, holding a DIFFERENT item (swap)
                          const cellItemId = cell?.booking_id || cell?.ical_event_id;
                          const canSwap = dragged && dragged.from_room !== r.id
                            && !!cellItemId && cellItemId !== dragged.id
                            && (status === "direct" || status === "ota");
                          const isHoverTarget = dragged && dragged.from_room !== r.id
                            && (canSwap || (status === "free" && !cell?.blocked));
                          return (
                            <td key={d.iso}
                              className={`relative border-b border-border/40 border-r border-border/20 p-0 ${isToday ? "ring-1 ring-primary/50 ring-inset" : ""} ${dragTarget === r.id && isHoverTarget ? (canSwap ? "ring-2 ring-primary ring-inset bg-primary/10" : "ring-2 ring-sage ring-inset bg-sage/5") : ""}`}
                              data-testid={`cell-${r.id}-${d.iso}`}
                              onDragOver={(e) => {
                                if (dragged && dragged.from_room !== r.id && (canSwap || (status === "free" && !cell?.blocked))) {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = "move";
                                  if (dragTarget !== r.id) setDragTarget(r.id);
                                }
                              }}
                              onDragLeave={() => setDragTarget((cur) => cur === r.id ? null : cur)}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDragTarget(null);
                                if (dragged?.id && dragged.from_room !== r.id) {
                                  if (canSwap) {
                                    const targetKind = cell.booking_id ? "booking" : "ical";
                                    swapBookings(dragged, { id: cellItemId, kind: targetKind });
                                  } else if (status === "free" && !cell?.blocked) {
                                    moveBooking(dragged, r.id);
                                  }
                                }
                                setDragged(null);
                              }}
                            >
                              <button type="button"
                                draggable={isDraggable}
                                onDragStart={(e) => {
                                  if (!isDraggable) return;
                                  const isIcal = status === "ota";
                                  const payload = {
                                    id: isIcal ? cell.ical_event_id : cell.booking_id,
                                    kind: isIcal ? "ical" : "booking",
                                    from_room: r.id,
                                    check_in: isIcal ? cell.ev_start : cell.check_in,
                                    check_out: isIcal ? cell.ev_end : cell.check_out,
                                    guest_name: cell.guest_name,
                                  };
                                  setDragged(payload);
                                  e.dataTransfer.effectAllowed = "move";
                                  try { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); } catch { /* noop */ }
                                  // Custom ghost preview — nicer than the default browser one.
                                  const ghost = document.createElement("div");
                                  const nights = Math.max(1, Math.round((new Date(payload.check_out) - new Date(payload.check_in)) / 86400000));
                                  ghost.setAttribute("data-testid", "drag-ghost");
                                  const bg = isIcal ? "#a13a2a" : "#C55A3C";
                                  ghost.style.cssText = `position:absolute;top:-1000px;left:-1000px;background:${bg};color:#F5EFE3;padding:10px 14px;border-radius:8px;font-family:Manrope,Arial,sans-serif;font-size:12px;line-height:1.3;box-shadow:0 12px 32px -8px rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.15);max-width:220px;pointer-events:none`;
                                  ghost.innerHTML = `
                                    <div style="letter-spacing:0.15em;text-transform:uppercase;font-size:9px;opacity:0.85;margin-bottom:4px;font-weight:600">${IT ? "Sposto" : "Moving"}${isIcal ? " · OTA" : ""}</div>
                                    <div style="font-weight:600;font-size:14px;margin-bottom:2px">${(payload.guest_name || "").slice(0, 24)}</div>
                                    <div style="opacity:0.85;font-size:11px">${payload.check_in} → ${payload.check_out}</div>
                                    <div style="opacity:0.85;font-size:11px;margin-top:2px">${nights} ${IT ? (nights === 1 ? "notte" : "notti") : (nights === 1 ? "night" : "nights")}</div>`;
                                  document.body.appendChild(ghost);
                                  try { e.dataTransfer.setDragImage(ghost, 20, 20); } catch { /* Safari */ }
                                  setTimeout(() => { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); }, 0);
                                }}
                                onDragEnd={() => { setDragged(null); setDragTarget(null); }}
                                onClick={() => {
                                  if (cell?.booking_id) setOpenedBookingId(cell.booking_id);
                                  else if (cell?.ical_event_id) setOpenedIcalId(cell.ical_event_id);
                                  else setSelected({ roomId: r.id, date: d.iso });
                                }}
                                className={`w-full h-full min-h-[52px] px-1 py-1 flex flex-col justify-between text-[10px] leading-tight transition-all ${statusCellClass(status)} ${isDraggable ? "cursor-grab active:cursor-grabbing" : ""} ${dragged?.id && (dragged.id === cell?.booking_id || dragged.id === cell?.ical_event_id) ? "opacity-40 scale-95" : ""}`}
                                title={cell?.guest_name ? `${cell.guest_name} · ${d.iso}${isDraggable ? (IT ? " · Trascina per spostare" : " · Drag to move") : ""}` : d.iso}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold">€{Math.round(price)}</span>
                                  {override && <span className="w-1 h-1 rounded-full bg-current opacity-70"/>}
                                </div>
                                <div className="flex items-center justify-between">
                                  {isFirstNight && cell.guest_name && (
                                    <span className="truncate text-[8px] font-medium opacity-90 max-w-[60px]">{cell.guest_name}</span>
                                  )}
                                  <div className="ml-auto flex items-center gap-0.5">
                                    {status === "blocked" && <Lock className="w-2.5 h-2.5 opacity-60"/>}
                                    {status === "direct" && <span className="text-[7px] uppercase font-bold opacity-80">D</span>}
                                    {status === "ota" && <span className="text-[7px] uppercase font-bold opacity-80">OTA</span>}
                                  </div>
                                </div>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Push rates to PMS */}
      {channels.length > 0 && (
        <div className="border border-border rounded-lg bg-card p-5" data-testid="calendar-push-rates-card">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold">
                {IT ? "Sincronizza tariffe con PMS / Channel Manager" : "Sync rates with PMS / Channel Manager"}
              </div>
              <div className="font-display text-lg mt-1">
                {IT ? "Push manuale dell'intero mese ai provider connessi" : "Manual push of the whole month to connected providers"}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest bg-sage/10 text-sage rounded-full px-2 py-1 font-semibold whitespace-nowrap" data-testid="auto-push-badge">
              {IT ? "Auto-push attivo" : "Auto-push on"}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {IT
              ? "Le tariffe si sincronizzano automaticamente ad ogni modifica. Usa i bottoni per un push forzato dell'intero mese di tutte le camere."
              : "Rates sync automatically on every edit. Use the buttons to force-push the entire month for all rooms."}
          </p>
          <div className="flex flex-wrap gap-2">
            {channels.map((ch) => (
              <Button key={ch.id} size="sm" variant="outline"
                onClick={() => pushRates(ch.id, ch.provider)}
                disabled={pushing === ch.id} className="lift"
                data-testid={`push-rates-${ch.provider}`}
              >
                <Send className="w-3.5 h-3.5 mr-1"/>
                {pushing === ch.id ? (IT ? "Invio…" : "Sending…") : `${ch.provider.charAt(0).toUpperCase() + ch.provider.slice(1)}${ch.mode === "sandbox" ? " (sandbox)" : ""}`}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* iCal Feed URLs — one per room */}
      <div className="border border-border rounded-lg bg-card p-5" data-testid="calendar-feed-card">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">
          {IT ? "Feed iCal per OTA (una URL per camera)" : "iCal feed for OTA (one URL per room)"}
        </div>
        <div className="font-display text-lg mb-1">
          {IT ? "Sincronizza la disponibilità con Booking, Airbnb, Vrbo" : "Sync availability with Booking, Airbnb, Vrbo"}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {IT
            ? "Copia l'URL della camera e incollalo nel backoffice OTA nella sezione \"Importa iCal\". ⚠️ iCal sincronizza solo la DISPONIBILITÀ, non i prezzi."
            : "Copy the room URL and paste it into the OTA \"Import iCal\" section. ⚠️ iCal syncs AVAILABILITY only, not prices."}
        </p>
        <div className="space-y-2">
          {rooms.map((r) => {
            const feed = feedUrls[r.id];
            return (
              <div key={r.id} className="flex items-center gap-2" data-testid={`feed-row-${r.id}`}>
                <div className="text-xs font-medium min-w-[160px] truncate">{r.name}</div>
                <Input readOnly value={feed?.url || ""} className="font-mono text-[11px] h-8" data-testid={`feed-url-${r.id}`}/>
                <Button size="sm" variant="outline" onClick={() => copyFeed(feed?.url)} disabled={!feed} data-testid={`feed-copy-${r.id}`}><Copy className="w-3.5 h-3.5"/></Button>
                <Button size="sm" variant="ghost" onClick={() => rotateFeed(r.id)} disabled={!feed} title={IT ? "Rigenera token" : "Rotate token"} data-testid={`feed-rotate-${r.id}`}><RefreshCw className="w-3.5 h-3.5"/></Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cell edit dialog */}
      <CellEditDialog
        selection={selected}
        room={selected ? rooms.find((r) => r.id === selected.roomId) : null}
        day={selected ? roomData[selected.roomId]?.byIso?.[selected.date] : null}
        defaultPrice={selected ? roomData[selected.roomId]?.room?.price_per_night : null}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); load(); }}
        onManualBooking={() => { if (selected) { openManual(selected.roomId, selected.date); setSelected(null); } }}
        hasChannels={channels.length > 0}
      />

      {/* Manual booking dialog */}
      <ManualBookingDialog
        rooms={rooms}
        defaultRoomId={prefill.roomId}
        prefillCheckIn={prefill.checkIn}
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={() => { setManualOpen(false); load(); }}
      />

      {/* Booking detail dialog (edit / cancel) */}
      <BookingDetailDialog
        bookingId={openedBookingId}
        rooms={rooms}
        onClose={() => setOpenedBookingId(null)}
        onChanged={() => { setOpenedBookingId(null); load(); }}
      />

      {/* OTA event detail dialog (read-only — iCal source is authoritative) */}
      <IcalEventDialog
        eventId={openedIcalId}
        onClose={() => setOpenedIcalId(null)}
      />

      {/* Bulk rate edit dialog (per-room) */}
      <BulkRatesDialog
        room={bulkForRoom}
        onClose={() => setBulkForRoom(null)}
        onSaved={() => { setBulkForRoom(null); load(); }}
      />

      {/* Season preset picker (per-room) */}
      <PresetPickerDialog
        room={presetForRoom}
        presets={presets}
        onClose={() => setPresetForRoom(null)}
        onApplied={() => { setPresetForRoom(null); load(); }}
        onManage={() => { setPresetForRoom(null); setManagePresetsOpen(true); }}
      />

      {/* Manage presets (create / edit / delete) */}
      <ManagePresetsDialog
        open={managePresetsOpen}
        presets={presets}
        onClose={() => { setManagePresetsOpen(false); loadPresets(); }}
        onChanged={loadPresets}
      />
    </div>
  );
}

// ---------- Cell edit dialog ----------
function CellEditDialog({ selection, room, day, defaultPrice, onClose, onSaved, onManualBooking, hasChannels }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [price, setPrice] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const open = !!selection;
  const date = selection?.date;
  const roomId = selection?.roomId;

  useEffect(() => {
    if (open) {
      setPrice(day?.price != null ? String(day.price) : (defaultPrice != null ? String(defaultPrice) : ""));
      setBlocked(!!day?.blocked);
    }
  }, [open, day?.date, day?.price, day?.blocked, defaultPrice]);

  if (!open) return null;
  const isBooked = day?.status === "direct" || day?.status === "ota";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { dates: [date] };
      const priceNum = parseFloat(price);
      if (!isNaN(priceNum) && priceNum > 0) payload.price = priceNum;
      payload.blocked = blocked;
      await api.put(`/calendar/room/${roomId}`, payload);
      toast.success(hasChannels
        ? (IT ? "Data aggiornata · sincronizzata con i PMS in background" : "Date updated · syncing to PMS in background")
        : (IT ? "Data aggiornata" : "Date updated"));
      onSaved();
    } catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
    finally { setSaving(false); }
  };
  const clearOverride = async () => {
    setSaving(true);
    try {
      await api.put(`/calendar/room/${roomId}`, { dates: [date], clear: true });
      toast.success(IT ? "Override rimosso" : "Override cleared");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`cell-edit-dialog-${roomId}-${date}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{room?.name} · {date}</DialogTitle>
          <DialogDescription>
            {isBooked ? (
              <span className="text-primary">{IT ? `Prenotata da ${day.guest_name || "guest"} (${day.source})` : `Booked by ${day.guest_name || "guest"} (${day.source})`}</span>
            ) : (IT ? "Imposta prezzo o blocca la data" : "Set price or block the date")}
          </DialogDescription>
        </DialogHeader>

        {!isBooked ? (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Euro className="w-3 h-3"/>{IT ? "Prezzo per questa notte" : "Price for this night"}</Label>
                <Input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={String(defaultPrice ?? "")} data-testid="cell-price-input"/>
                <p className="text-[10px] text-muted-foreground mt-1">{IT ? `Default camera: €${defaultPrice ?? "-"}` : `Room default: €${defaultPrice ?? "-"}`}</p>
              </div>
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer hover:border-primary/40" data-testid="cell-blocked-toggle">
                <input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)}/>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    {blocked ? <Lock className="w-3.5 h-3.5"/> : <Unlock className="w-3.5 h-3.5"/>}
                    {IT ? "Blocca questa data" : "Block this date"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{IT ? "La data non sarà prenotabile e verrà esposta come occupata alle OTA" : "Date won't be bookable and will be exposed as busy to OTAs"}</div>
                </div>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 justify-between pt-2">
              <div className="flex gap-2">
                {day?.has_override && (
                  <Button size="sm" variant="ghost" onClick={clearOverride} disabled={saving} data-testid="cell-clear-btn">
                    {IT ? "Ripristina default" : "Reset to default"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onManualBooking} data-testid="cell-manual-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1"/>{IT ? "Prenotazione manuale" : "Manual booking"}
                </Button>
              </div>
              <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="cell-save-btn">
                {saving ? "…" : (IT ? "Salva" : "Save")}
              </Button>
            </div>
            {hasChannels && (
              <div className="text-[10px] text-sage text-right pt-1 flex items-center justify-end gap-1" data-testid="cell-autopush-hint">
                <Send className="w-3 h-3"/>
                {IT ? "Auto-sync con Booking/Airbnb via PMS attivo" : "Auto-sync to Booking/Airbnb via PMS enabled"}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground bg-sand border border-border rounded-md p-3 flex gap-2 items-start">
            <Info className="w-4 h-4 mt-0.5 shrink-0"/>
            <div>{IT
              ? "Le date già prenotate non possono essere modificate qui. Cancella la prenotazione dalla tab Prenotazioni per liberare la data."
              : "Booked dates can't be edited here. Cancel the booking from the Bookings tab to free the date."}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Manual booking dialog (unchanged core) ----------
function ManualBookingDialog({ rooms, defaultRoomId, prefillCheckIn, open, onClose, onCreated }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [form, setForm] = useState({
    room_id: defaultRoomId || "",
    check_in: prefillCheckIn || "",
    check_out: "",
    guest_name: "", guest_email: "", guest_phone: "",
    channel: "walk_in", amount: "", notes: "",
    paid_on_create: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      let co = "";
      if (prefillCheckIn) {
        const d = new Date(prefillCheckIn); d.setDate(d.getDate() + 1);
        co = d.toISOString().slice(0, 10);
      }
      setForm({
        room_id: defaultRoomId || "", check_in: prefillCheckIn || "", check_out: co,
        guest_name: "", guest_email: "", guest_phone: "",
        channel: "walk_in", amount: "", notes: "",
        paid_on_create: false,
      });
    }
  }, [open, defaultRoomId, prefillCheckIn]);

  const submit = async () => {
    if (!form.room_id || !form.check_in || !form.check_out || !form.guest_name) {
      toast.error(IT ? "Compila camera, date e nome ospite" : "Fill room, dates and guest name");
      return;
    }
    if (!form.guest_phone || form.guest_phone.trim().length < 6) {
      toast.error(IT ? "Il telefono ospite è obbligatorio" : "Guest phone is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        room_id: form.room_id, check_in: form.check_in, check_out: form.check_out,
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email.trim() || null,
        guest_phone: form.guest_phone.trim() || null,
        channel: form.channel,
        notes: form.notes.trim() || null,
        paid_on_create: !!form.paid_on_create,
      };
      const amt = parseFloat(form.amount);
      if (!isNaN(amt) && amt > 0) payload.amount = amt;
      const r = await api.post("/bookings/manual", payload);
      const b = r.data.booking;
      toast.success(IT ? `Prenotazione manuale creata (${b.nights} notti · €${b.amount})` : `Manual booking created (${b.nights} nights · €${b.amount})`);
      // Auto-distribution: when the requested room was busy the backend moved
      // the booking to a same-capacity sibling. Surface it so the host knows.
      if (r.data.redirected && r.data.original_room_id) {
        const orig = rooms.find((rm) => rm.id === r.data.original_room_id);
        const origName = orig?.name || (IT ? "camera richiesta" : "requested room");
        toast.warning(
          IT
            ? `Assegnato a ${b.room_name} invece di ${origName} (occupata negli stessi giorni)`
            : `Assigned to ${b.room_name} instead of ${origName} (busy on the same dates)`,
          { duration: 7000, id: `redirect-${b.id}` }
        );
      }
      onCreated();
    } catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manual-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{IT ? "Nuova prenotazione manuale" : "New manual booking"}</DialogTitle>
          <DialogDescription>
            {IT ? "Registra una prenotazione ricevuta al telefono, in loco o via email — verrà bloccata in calendario e sincronizzata con le OTA." : "Register a booking received by phone, on-site or via email — will block the calendar and sync to OTAs."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{IT ? "Camera" : "Room"}</Label>
            <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
              <SelectTrigger data-testid="manual-room"><SelectValue placeholder={IT ? "Seleziona camera" : "Select room"}/></SelectTrigger>
              <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="manual-checkin"/></div>
            <div><Label>Check-out</Label><Input type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="manual-checkout"/></div>
          </div>
          <div><Label>{IT ? "Nome ospite" : "Guest name"}</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Mario Rossi" data-testid="manual-guest-name"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{IT ? "Email" : "Email"} <span className="text-muted-foreground text-[10px]">({IT ? "opzionale" : "optional"})</span></Label><Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="manual-guest-email"/></div>
            <div><Label>{IT ? "Telefono" : "Phone"} <span className="text-destructive text-[10px]">*</span></Label><Input required value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} data-testid="manual-guest-phone"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{IT ? "Canale" : "Channel"}</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="manual-channel"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk_in">{IT ? "Walk-in / di persona" : "Walk-in"}</SelectItem>
                  <SelectItem value="phone">{IT ? "Telefono" : "Phone"}</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="social">{IT ? "Social / WhatsApp" : "Social / WhatsApp"}</SelectItem>
                  <SelectItem value="other">{IT ? "Altro" : "Other"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{IT ? "Importo €" : "Amount €"} <span className="text-muted-foreground text-[10px]">({IT ? "auto se vuoto" : "auto if empty"})</span></Label><Input type="number" min="0" step="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={IT ? "Auto-calcolato" : "Auto-computed"} data-testid="manual-amount"/></div>
          </div>
          <div><Label>{IT ? "Note interne" : "Internal notes"}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="manual-notes"/></div>
          <label className="flex items-start gap-2 p-3 border border-border rounded-md bg-sand/40 cursor-pointer" data-testid="manual-paid-toggle-row">
            <input type="checkbox" checked={!!form.paid_on_create} onChange={(e)=>setForm({ ...form, paid_on_create: e.target.checked })} data-testid="manual-paid-on-create" className="mt-0.5 accent-primary w-4 h-4"/>
            <div className="text-[12px] leading-relaxed">
              <div className="font-semibold mb-0.5">{IT ? "Pagamento già ricevuto" : "Payment already received"}</div>
              <div className="text-muted-foreground text-[11px]">
                {IT
                  ? "Spunta solo se hai già incassato (contanti / bonifico / POS). Verrà emesso subito il documento fiscale. Lascia vuoto per creare come 'da incassare'."
                  : "Check only if you've already collected the payment (cash / transfer / POS). The fiscal document will be issued immediately. Leave unchecked to create as 'to be collected'."}
              </div>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>{IT ? "Annulla" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="manual-submit">
            {saving ? "…" : (IT ? "Crea prenotazione" : "Create booking")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Booking detail dialog (open from calendar) ----------
const PAYMENT_STATUS_LABEL = (ps, IT) => {
  if (!ps) return "";
  const map = IT
    ? { paid: "Pagata", pending: "Da incassare", guaranteed: "Garantita", guarantee_pending: "Attesa carta", cancelled: "Cancellata", no_show_charged: "No-show addebitato" }
    : { paid: "Paid", pending: "To collect", guaranteed: "Guaranteed", guarantee_pending: "Awaiting card", cancelled: "Cancelled", no_show_charged: "No-show charged" };
  return map[ps] || ps;
};
const PAYMENT_STATUS_TONE = (ps) => {
  switch (ps) {
    case "paid": return "bg-sage text-white border-sage";
    case "pending": return "bg-amber-500/20 text-amber-700 border-amber-500/40";
    case "guaranteed": return "bg-primary/15 text-primary border-primary/30";
    case "cancelled": return "bg-muted text-muted-foreground border-border";
    case "no_show_charged": return "bg-destructive text-white border-destructive";
    default: return "bg-muted text-muted-foreground border-border";
  }
};

export function BookingDetailDialog({ bookingId, rooms, onClose, onChanged }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const open = !!bookingId;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPs, setSavingPs] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [b, setB] = useState(null);
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [form, setForm] = useState({});
  // Review state — summary badge + inline dialog. Fetched lazily whenever
  // we open a booking whose check_out is in the past AND has a guest_email.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null); // { avg, count, tags[] }
  const [myReview, setMyReview] = useState(null);           // review this host wrote for THIS booking

  const loadReviewMeta = async (booking) => {
    if (!booking?.guest_email) { setReviewSummary(null); setMyReview(null); return; }
    try {
      const [s, mine] = await Promise.all([
        api.get(`/guest-reviews/summary/${encodeURIComponent(booking.guest_email)}`),
        api.get(`/guest-reviews/for-booking/${booking.id}`),
      ]);
      setReviewSummary(s.data);
      setMyReview(mine.data || null);
    } catch { setReviewSummary(null); setMyReview(null); }
  };

  useEffect(() => {
    if (!open) { setB(null); setMode("view"); setReviewSummary(null); setMyReview(null); return; }
    let alive = true;
    setLoading(true);
    api.get(`/bookings/${bookingId}`)
      .then((r) => {
        if (!alive) return;
        setB(r.data);
        setForm({
          room_id: r.data.room_id || "",
          check_in: (r.data.check_in || "").slice(0, 10),
          check_out: (r.data.check_out || "").slice(0, 10),
          guest_name: r.data.guest_name || "",
          guest_email: r.data.guest_email || "",
          guest_phone: r.data.guest_phone || "",
          guests: r.data.guests != null ? String(r.data.guests) : "",
          amount: r.data.amount != null ? String(r.data.amount) : "",
          notes: r.data.notes || "",
          arrival_time: r.data.arrival_time || "",
        });
        setMode("view");
        loadReviewMeta(r.data);
      })
      .catch((e) => { toast.error(errMsg(e, IT ? "Errore" : "Error")); onClose(); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  if (!open) return null;
  const isOta = (b?.source || "").toLowerCase() === "ical";
  const isCancelled = b?.status === "cancelled";
  const canEdit = !!b && !isOta && !isCancelled;
  const canMarkPaid = !!b && b.payment_status === "pending" && !isCancelled;
  // Reviews only make sense once the guest has actually left and only for
  // direct bookings (OTA guests don't carry a persistent email in most
  // channels — the OTA is the source of truth).
  const checkoutPast = !!b && b.check_out && new Date(b.check_out + "T23:59:59") < new Date();
  const canReview = !!b && !isOta && !isCancelled && checkoutPast && !!b.guest_email;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        room_id: form.room_id || null,
        check_in: form.check_in || null,
        check_out: form.check_out || null,
        guest_name: form.guest_name || null,
        guest_email: form.guest_email || null,
        guest_phone: form.guest_phone || null,
        notes: form.notes || null,
      };
      const amt = parseFloat(form.amount);
      if (!isNaN(amt)) payload.amount = amt;
      // Guests: empty → null (clears); 1-20 → persisted verbatim on the booking doc.
      const gTrim = (form.guests || "").toString().trim();
      if (gTrim !== "") {
        const g = parseInt(gTrim, 10);
        if (!Number.isFinite(g) || g < 0 || g > 20) {
          toast.error(IT ? "Numero ospiti non valido (0–20)" : "Invalid guests (0–20)");
          setSaving(false);
          return;
        }
        payload.guests = g;
      }
      const r = await api.put(`/bookings/${bookingId}`, payload);
      // Persist arrival_time via the dedicated PATCH — kept out of the PUT
      // payload so we reuse the same server-side normaliser the Reception
      // inline editor + the public booking form already rely on.
      let updatedArrival = b?.arrival_time || null;
      const nextArrival = (form.arrival_time || "").trim() || "";
      if (nextArrival !== (b?.arrival_time || "")) {
        try {
          const pr = await api.patch(`/bookings/${bookingId}/arrival-time`, { arrival_time: nextArrival });
          updatedArrival = pr.data?.arrival_time ?? (nextArrival || null);
        } catch (e) {
          toast.error(errMsg(e, IT ? "Errore orario di arrivo" : "Arrival time error"));
        }
      }
      toast.success(IT ? "Prenotazione aggiornata" : "Booking updated");
      setB({ ...(r.data.booking || b), arrival_time: updatedArrival });
      setMode("view");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore aggiornamento" : "Update error"));
    } finally { setSaving(false); }
  };

  const markPaid = async () => {
    try {
      await api.post(`/bookings/${bookingId}/mark-paid`, {});
      toast.success(IT ? "Prenotazione segnata come pagata" : "Booking marked as paid");
      onChanged();
    } catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
  };

  const changePaymentStatus = async (v) => {
    setForm((prev) => ({ ...prev, payment_status: v }));
    setSavingPs(true);
    try {
      await api.patch(`/bookings/${bookingId}/payment-status`, { payment_status: v });
      toast.success(IT ? "Stato pagamento aggiornato" : "Payment status updated");
      // Refresh local booking so the pill outside edit mode reflects the change
      const r = await api.get(`/bookings/${bookingId}`);
      setB(r.data);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingPs(false); }
  };

  const cancelBooking = async () => {
    const wasPaid = b?.payment_status === "paid";
    const msg = wasPaid
      ? (IT
        ? "La prenotazione è già stata pagata. Cancellandola libererai la data ma NON verrà emesso alcun rimborso automatico. Procedere?"
        : "This booking has been paid. Cancelling will free the date but NO automatic refund will be issued. Continue?")
      : (IT ? "Cancellare questa prenotazione?" : "Cancel this booking?");
    if (!window.confirm(msg)) return;
    setCancelling(true);
    try {
      await api.delete(`/bookings/${bookingId}`);
      toast.success(IT ? "Prenotazione cancellata" : "Booking cancelled");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setCancelling(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`booking-detail-dialog-${bookingId}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Dettaglio prenotazione" : "Booking detail"}
          </DialogTitle>
          <DialogDescription>
            {isOta
              ? (IT
                ? "Prenotazione importata da OTA — la modifica deve avvenire sul portale della OTA di origine."
                : "OTA-imported booking — please edit it on the source channel.")
              : (IT
                ? "Aggiorna i dettagli oppure cancella la prenotazione per liberare la data."
                : "Update the details or cancel to free the calendar date.")}
          </DialogDescription>
        </DialogHeader>

        {loading || !b ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : mode === "edit" ? (
          <div className="space-y-3">
            <div>
              <Label>{IT ? "Camera" : "Room"}</Label>
              <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                <SelectTrigger data-testid="detail-edit-room"><SelectValue/></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{IT ? "Numero ospiti" : "Guests"}</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={20}
                  step={1}
                  value={form.guests || ""}
                  onChange={(e) => setForm({ ...form, guests: e.target.value })}
                  placeholder={IT ? "es. 2" : "e.g. 2"}
                  data-testid="detail-edit-guests"
                />
              </div>
              <div>
                <Label>{IT ? "Notti" : "Nights"}</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={365}
                  step={1}
                  value={(() => {
                    try {
                      const ms = new Date(form.check_out) - new Date(form.check_in);
                      const n = Math.max(1, Math.round(ms / 86400000));
                      return Number.isFinite(n) ? n : "";
                    } catch { return ""; }
                  })()}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!Number.isFinite(n) || n < 1 || n > 365) return;
                    if (!form.check_in) return;
                    // Recompute check_out from check_in + N nights so the two
                    // sources of truth (dates vs nights) stay consistent —
                    // the backend recomputes nights on save from the date range.
                    const ci = new Date(form.check_in + "T00:00:00");
                    if (Number.isNaN(ci.getTime())) return;
                    const co = new Date(ci.getTime() + n * 86400000);
                    const iso = `${co.getFullYear()}-${String(co.getMonth() + 1).padStart(2, "0")}-${String(co.getDate()).padStart(2, "0")}`;
                    setForm({ ...form, check_out: iso });
                  }}
                  className="font-mono"
                  data-testid="detail-edit-nights"
                />
              </div>
              <div>
                <Label>{IT ? "Canale" : "Channel"}</Label>
                <Input
                  type="text"
                  value={b?.channel || b?.source || "diretta"}
                  readOnly
                  disabled
                  className="bg-muted/50 text-xs"
                  data-testid="detail-edit-channel"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="detail-edit-checkin"/></div>
              <div><Label>Check-out</Label><Input type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="detail-edit-checkout"/></div>
            </div>
            <div><Label>{IT ? "Nome ospite" : "Guest name"}</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="detail-edit-guest-name"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="detail-edit-guest-email"/></div>
              <div><Label>{IT ? "Telefono" : "Phone"}</Label><Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} data-testid="detail-edit-guest-phone"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{IT ? "Importo €" : "Amount €"}</Label><Input type="number" min="0" step="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="detail-edit-amount"/></div>
              <div>
                <Label>{IT ? "Orario di arrivo" : "Arrival time"}</Label>
                <Input
                  type="time"
                  value={form.arrival_time || ""}
                  onChange={(e) => setForm({ ...form, arrival_time: e.target.value })}
                  data-testid="detail-edit-arrival-time"
                />
              </div>
            </div>
            <div>
              <Label>{IT ? "Stato pagamento" : "Payment status"}</Label>
              <select
                value={form.payment_status ?? b.payment_status ?? "pending"}
                onChange={(e) => changePaymentStatus(e.target.value)}
                disabled={savingPs}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="detail-edit-payment-status"
              >
                <option value="pending">{IT ? "Pendente" : "Pending"}</option>
                <option value="paid">{IT ? "Pagato" : "Paid"}</option>
                <option value="refunded">{IT ? "Rimborsato" : "Refunded"}</option>
                <option value="no_show_charged">{IT ? "Penale incassata" : "Penalty charged"}</option>
              </select>
            </div>
            <div><Label>{IT ? "Note interne" : "Internal notes"}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="detail-edit-notes"/></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMode("view")} data-testid="detail-edit-cancel">{IT ? "Annulla" : "Cancel"}</Button>
              <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="detail-edit-save">
                {saving ? "…" : (IT ? "Salva modifiche" : "Save changes")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-base">{b.guest_name || "—"}</div>
              <span
                className={`text-[11px] px-2 py-1 rounded-full border ${PAYMENT_STATUS_TONE(b.payment_status)} whitespace-nowrap`}
                data-testid="detail-payment-status"
              >{PAYMENT_STATUS_LABEL(b.payment_status, IT)}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 border border-border rounded-md p-3 bg-sand/40" data-testid="detail-inline-triple">
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Camera" : "Room"}</div>
                <div data-testid="detail-view-room">{b.room_name || "—"}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Numero ospiti" : "Guests"}</div>
                <div data-testid="detail-view-guests">{b.guests != null ? b.guests : "—"}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Notti" : "Nights"}</div>
                <div data-testid="detail-view-nights">{b.nights}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 border border-border rounded-md p-3 bg-sand/40">
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Importo" : "Amount"}</div>
                <div>€ {Number(b.amount || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Canale" : "Channel"}</div>
                <div>{b.channel || b.source || "—"}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">Check-in</div>
                <div>{b.check_in}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">Check-out</div>
                <div>{b.check_out}</div>
              </div>
            </div>
            {(b.city_tax_amount != null && Number(b.city_tax_amount) > 0) && (
              <div
                className="border border-primary/25 rounded-md p-3 bg-primary/5 flex items-start gap-3 text-xs"
                data-testid="detail-city-tax"
                title={IT
                  ? "Contabilizzata separatamente — non concorre a commissioni HH né IVA sull'alloggio."
                  : "Booked separately — excluded from HH commission and lodging VAT."}
              >
                <div className="uppercase tracking-widest text-[10px] text-primary font-semibold shrink-0">
                  {IT ? "Imposta di soggiorno" : "City tax"}
                </div>
                <div className="flex-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-mono text-sm font-semibold text-primary" data-testid="detail-city-tax-amount">
                    € {Number(b.city_tax_amount).toFixed(2)}
                  </span>
                  {b.city_tax_breakdown && (
                    <span className="text-[10px] text-muted-foreground">
                      {IT
                        ? `${b.city_tax_breakdown.taxable_guests} ospiti × ${b.city_tax_breakdown.nights_charged} notti × € ${Number(b.city_tax_breakdown.amount_per_person_per_night).toFixed(2)}${b.city_tax_breakdown.exempt_guests ? ` · ${b.city_tax_breakdown.exempt_guests} esente/i (età)` : ""}`
                        : `${b.city_tax_breakdown.taxable_guests} guests × ${b.city_tax_breakdown.nights_charged} nights × € ${Number(b.city_tax_breakdown.amount_per_person_per_night).toFixed(2)}${b.city_tax_breakdown.exempt_guests ? ` · ${b.city_tax_breakdown.exempt_guests} exempt (age)` : ""}`}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground italic">
                    {IT ? "· contabilità separata" : "· separate accounting"}
                  </span>
                </div>
              </div>
            )}
            {(b.guest_email || b.guest_phone) && (
              <div className="text-xs text-muted-foreground">
                {b.guest_email && <div>✉ {b.guest_email}</div>}
                {b.guest_phone && <div>☎ {b.guest_phone}</div>}
              </div>
            )}
            {b.notes && (
              <div className="border-l-2 border-primary/40 pl-3 text-xs italic text-muted-foreground">
                {b.notes}
              </div>
            )}
            {/* Guest reputation badge — only for direct bookings with an email,
                even before checkout so the host can screen upcoming arrivals. */}
            {!isOta && b.guest_email && reviewSummary && reviewSummary.count > 0 && (
              <div className="border border-primary/25 rounded-md p-3 bg-primary/5 text-xs flex items-start gap-3" data-testid="detail-review-summary">
                <div className="flex items-center gap-1 shrink-0">
                  <Star className="w-4 h-4 fill-primary text-primary"/>
                  <span className="font-semibold text-sm text-primary" data-testid="detail-review-avg">{reviewSummary.avg}</span>
                  <span className="text-muted-foreground">/5</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
                    {IT ? "Reputazione ospite" : "Guest reputation"}
                  </div>
                  <div className="text-muted-foreground">
                    {IT
                      ? <>Recensito da <b>{reviewSummary.count}</b> {reviewSummary.count === 1 ? "host" : "host"} sulla rete</>
                      : <>Rated by <b>{reviewSummary.count}</b> {reviewSummary.count === 1 ? "host" : "hosts"} on the network</>}
                  </div>
                  {reviewSummary.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {reviewSummary.tags.map((t) => (
                        <span key={t.tag} className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 border border-primary/20 text-primary/90">
                          {t.tag.replace(/_/g, " ")} ×{t.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {isOta && (
              <div className="p-3 border border-border bg-muted/40 rounded-md text-xs text-muted-foreground">
                {IT
                  ? "Le prenotazioni importate via iCal/OTA sono di sola lettura. Modifica o cancella dalla dashboard OTA di origine — la sincronizzazione aggiornerà HospitalityHub."
                  : "iCal/OTA-imported bookings are read-only. Please edit or cancel from the source OTA — sync will update HospitalityHub."}
              </div>
            )}
            {isCancelled && (
              <div className="p-3 border border-border bg-muted/40 rounded-md text-xs text-muted-foreground">
                {IT ? "Questa prenotazione è stata cancellata" : "This booking is cancelled"}
                {b.cancelled_at && ` · ${new Date(b.cancelled_at).toLocaleDateString(IT ? "it-IT" : "en-US")}`}
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {canReview && (
                <Button
                  variant="outline"
                  onClick={() => setReviewOpen(true)}
                  className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground"
                  data-testid="detail-review-btn"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1"/>
                  {myReview
                    ? (IT ? "Modifica recensione" : "Edit review")
                    : (IT ? "Recensisci ospite" : "Review guest")}
                </Button>
              )}
              {canMarkPaid && (
                <Button
                  variant="outline"
                  onClick={markPaid}
                  className="border-sage/50 text-sage hover:bg-sage hover:text-white"
                  data-testid="detail-mark-paid-btn"
                >
                  {IT ? "Segna pagata" : "Mark paid"}
                </Button>
              )}
              {canEdit && (
                <Button
                  onClick={() => setMode("edit")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
                  data-testid="detail-edit-btn"
                >
                  {IT ? "Modifica" : "Edit"}
                </Button>
              )}
              {!isCancelled && (
                <Button
                  variant="outline"
                  onClick={cancelBooking}
                  disabled={cancelling}
                  className="border-destructive/40 text-destructive hover:bg-destructive hover:text-white"
                  data-testid="detail-cancel-btn"
                >
                  {cancelling ? "…" : (IT ? "Cancella prenotazione" : "Cancel booking")}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <GuestReviewDialog
        open={reviewOpen}
        booking={b}
        onClose={() => setReviewOpen(false)}
        onSaved={() => loadReviewMeta(b)}
      />
    </Dialog>
  );
}

// ---------- Bulk rate edit dialog (per-room) ----------
function BulkRatesDialog({ room, onClose, onSaved }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const open = !!room;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => new Date(), [open]);
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const [from, setFrom] = useState(() => toISO(today));
  const [to, setTo] = useState(() => { const d = new Date(today); d.setDate(d.getDate()+30); return toISO(d); });
  const [weekdays, setWeekdays] = useState([true, true, true, true, true, true, true]);
  const [mode, setMode] = useState("price");
  const [price, setPrice] = useState(room?.price_per_night ? String(room.price_per_night) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFrom(toISO(today));
    const d = new Date(today); d.setDate(d.getDate()+30); setTo(toISO(d));
    setWeekdays([true, true, true, true, true, true, true]);
    setMode("price");
    setPrice(room?.price_per_night ? String(room.price_per_night) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, room?.id]);

  const isoDates = useMemo(() => {
    if (!open) return [];
    try {
      const a = new Date(from); const b = new Date(to);
      if (isNaN(a) || isNaN(b) || a > b) return [];
      const out = [];
      const cur = new Date(a);
      while (cur <= b) {
        const wd = (cur.getDay() + 6) % 7; // Mon=0..Sun=6
        if (weekdays[wd]) out.push(toISO(cur));
        cur.setDate(cur.getDate() + 1);
      }
      return out;
    } catch { return []; }
  }, [open, from, to, weekdays]);

  const toggleWd = (i) => setWeekdays((prev) => prev.map((v, idx) => idx === i ? !v : v));
  const setAllWd = (v) => setWeekdays([v, v, v, v, v, v, v]);
  const applyWeekdaysOnly = () => setWeekdays([true, true, true, true, true, false, false]);
  const applyWeekendOnly = () => setWeekdays([false, false, false, false, false, true, true]);

  const submit = async () => {
    if (!isoDates.length) {
      toast.error(IT ? "Nessuna data selezionata" : "No dates selected");
      return;
    }
    let payload = { dates: isoDates };
    if (mode === "price") {
      const p = parseFloat(price);
      if (isNaN(p) || p < 0) { toast.error(IT ? "Prezzo non valido" : "Invalid price"); return; }
      payload.price = p;
    } else if (mode === "block") {
      payload.blocked = true;
    } else if (mode === "unblock") {
      payload.blocked = false;
    } else if (mode === "clear") {
      payload.clear = true;
    }
    setSaving(true);
    try {
      await api.put(`/calendar/room/${room.id}`, payload);
      toast.success(IT ? `${isoDates.length} giorni aggiornati` : `${isoDates.length} days updated`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore aggiornamento" : "Update error"));
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const WD = IT ? ["L","M","M","G","V","S","D"] : ["M","T","W","T","F","S","S"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-rates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Modifica tariffe in blocco" : "Bulk rate edit"}
          </DialogTitle>
          <DialogDescription>
            {room?.name} · {IT ? "prezzo base" : "base price"} €{Math.round(room?.price_per_night || 0)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{IT ? "Dal" : "From"}</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-from"/>
            </div>
            <div>
              <Label>{IT ? "Al" : "To"}</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-to"/>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <Label>{IT ? "Giorni della settimana" : "Weekdays"}</Label>
              <div className="flex gap-2 text-[11px]">
                <button type="button" onClick={() => setAllWd(true)} className="text-primary hover:underline">{IT ? "Tutti" : "All"}</button>
                <button type="button" onClick={applyWeekdaysOnly} className="text-primary hover:underline">{IT ? "Feriali" : "Weekdays"}</button>
                <button type="button" onClick={applyWeekendOnly} className="text-primary hover:underline">{IT ? "Weekend" : "Weekend"}</button>
              </div>
            </div>
            <div className="flex gap-1">
              {WD.map((label, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => toggleWd(i)}
                  data-testid={`bulk-wd-${i}`}
                  className={`w-9 h-9 rounded-md border text-sm font-semibold transition-all ${
                    weekdays[i] ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"
                  }`}
                >{label}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>{IT ? "Azione" : "Action"}</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="bulk-mode"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="price">{IT ? "Imposta prezzo €" : "Set price €"}</SelectItem>
                <SelectItem value="block">{IT ? "Blocca (chiudi)" : "Block (close)"}</SelectItem>
                <SelectItem value="unblock">{IT ? "Sblocca" : "Unblock"}</SelectItem>
                <SelectItem value="clear">{IT ? "Rimuovi override (torna al prezzo base)" : "Clear override (revert to base)"}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "price" && (
            <div>
              <Label>{IT ? "Prezzo per notte (€)" : "Price per night (€)"}</Label>
              <Input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="bulk-price"/>
            </div>
          )}

          <div className="border border-border rounded-md p-3 bg-sand/40 text-sm" data-testid="bulk-preview">
            <div className="uppercase tracking-widest text-[10px] text-sage font-semibold mb-1">{IT ? "Anteprima" : "Preview"}</div>
            <div>
              <span className="font-semibold">{isoDates.length}</span> {IT ? "giorni saranno aggiornati" : "days will be updated"}
              {isoDates.length > 0 && (
                <span className="text-muted-foreground"> · {isoDates[0]} → {isoDates[isoDates.length-1]}</span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose}>{IT ? "Annulla" : "Cancel"}</Button>
            <Button
              onClick={submit}
              disabled={saving || isoDates.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
              data-testid="bulk-apply"
            >
              {saving ? "…" : (IT ? `Applica a ${isoDates.length} giorni` : `Apply to ${isoDates.length} days`)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Preset picker dialog (choose preset → apply to room) ----------
function PresetPickerDialog({ room, presets, onClose, onApplied, onManage }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const open = !!room;
  const [applying, setApplying] = useState(null); // preset id being applied

  const applyPreset = async (preset) => {
    setApplying(preset.id);
    try {
      const r = await api.post(`/season-presets/${preset.id}/apply`, { room_id: room.id });
      toast.success(IT ? `Preset applicato a ${r.data.applied_days} giorni` : `Preset applied to ${r.data.applied_days} days`);
      onApplied();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setApplying(null); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="preset-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Applica preset stagionale" : "Apply season preset"}
          </DialogTitle>
          <DialogDescription>
            {room?.name} · {IT ? "un click applica prezzo e periodi salvati" : "one click applies saved price + periods"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {presets.length === 0 && (
            <div className="p-4 text-center border border-dashed border-border rounded-md text-sm text-muted-foreground" data-testid="preset-empty">
              {IT
                ? "Nessun preset ancora. Crea la tua prima stagione (Alta, Media, Bassa) per riapplicarla in un click."
                : "No presets yet. Create your first season (High, Mid, Low) to re-apply it in one click."}
            </div>
          )}
          {presets.map((p) => {
            const days = (p.ranges || []).reduce((s, r) => {
              try { return s + Math.max(0, Math.round((new Date(r.to) - new Date(r.from)) / 86400000) + 1); } catch { return s; }
            }, 0);
            const activeWd = (p.weekdays || []).filter(Boolean).length;
            return (
              <div key={p.id} data-testid={`preset-row-${p.id}`} className="flex items-center justify-between gap-2 border border-border rounded-md p-3 bg-sand/30">
                <div className="min-w-0">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    €{p.price}/{IT ? "notte" : "night"} · {days} {IT ? "giorni tot." : "days"} · {activeWd}/7 {IT ? "giorni sett." : "weekdays"}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => applyPreset(p)}
                  disabled={applying === p.id}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
                  data-testid={`preset-apply-${p.id}`}
                >
                  {applying === p.id ? "…" : (IT ? "Applica" : "Apply")}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between gap-2 pt-3 border-t border-border">
          <Button variant="outline" onClick={onClose}>{IT ? "Chiudi" : "Close"}</Button>
          <Button variant="outline" onClick={onManage} data-testid="preset-manage-btn">
            {IT ? "Gestisci preset" : "Manage presets"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Manage presets dialog ----------
function ManagePresetsDialog({ open, presets, onClose, onChanged }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [editing, setEditing] = useState(null); // null | preset (existing) | {} (new)
  const isNew = editing && !editing.id;

  const startNew = () => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
    setEditing({
      name: "",
      price: 150,
      weekdays: [true, true, true, true, true, true, true],
      ranges: [{ from: iso(today), to: iso(in30) }],
    });
  };
  const save = async () => {
    if (!editing.name?.trim()) { toast.error(IT ? "Inserisci un nome" : "Enter a name"); return; }
    if (!editing.ranges?.length) { toast.error(IT ? "Aggiungi almeno un intervallo" : "Add at least one range"); return; }
    try {
      const payload = {
        name: editing.name.trim(),
        price: Number(editing.price) || 0,
        weekdays: editing.weekdays,
        ranges: editing.ranges,
      };
      if (editing.id) await api.put(`/season-presets/${editing.id}`, payload);
      else await api.post("/season-presets", payload);
      toast.success(IT ? "Preset salvato" : "Preset saved");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
  };
  const del = async (p) => {
    if (!window.confirm(IT ? `Eliminare "${p.name}"?` : `Delete "${p.name}"?`)) return;
    try { await api.delete(`/season-presets/${p.id}`); toast.success(IT ? "Eliminato" : "Deleted"); onChanged(); }
    catch (e) { toast.error(errMsg(e, IT ? "Errore" : "Error")); }
  };

  if (!open) return null;
  const WD = IT ? ["L","M","M","G","V","S","D"] : ["M","T","W","T","F","S","S"];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manage-presets-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Gestisci preset stagionali" : "Manage season presets"}
          </DialogTitle>
          <DialogDescription>
            {IT ? "Crea Alta / Media / Bassa stagione con date e prezzi personalizzati." : "Create High / Mid / Low seasons with custom dates and prices."}
          </DialogDescription>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-2">
            {presets.map((p) => (
              <div key={p.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`manage-preset-row-${p.id}`}>
                <div>
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="text-xs text-muted-foreground">€{p.price} · {(p.ranges || []).length} {IT ? "intervalli" : "ranges"}</div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`preset-edit-${p.id}`}>{IT ? "Modifica" : "Edit"}</Button>
                  <Button size="sm" variant="outline" onClick={() => del(p)} className="border-destructive/40 text-destructive hover:bg-destructive hover:text-white" data-testid={`preset-delete-${p.id}`}>{IT ? "Elimina" : "Delete"}</Button>
                </div>
              </div>
            ))}
            <Button onClick={startNew} className="w-full mt-2 bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="preset-new-btn">
              + {IT ? "Nuovo preset" : "New preset"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>{IT ? "Nome (es. Alta stagione)" : "Name (e.g. High season)"}</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="preset-form-name"/>
            </div>
            <div>
              <Label>{IT ? "Prezzo per notte (€)" : "Price per night (€)"}</Label>
              <Input type="number" min="0" step="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="preset-form-price"/>
            </div>
            <div>
              <Label>{IT ? "Giorni della settimana" : "Weekdays"}</Label>
              <div className="flex gap-1">
                {WD.map((label, i) => (
                  <button
                    type="button" key={i}
                    onClick={() => setEditing({ ...editing, weekdays: editing.weekdays.map((v, idx) => idx === i ? !v : v) })}
                    className={`w-9 h-9 rounded-md border text-sm font-semibold transition-all ${
                      editing.weekdays[i] ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"
                    }`}
                    data-testid={`preset-form-wd-${i}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <Label>{IT ? "Intervalli di date" : "Date ranges"}</Label>
                <button type="button" onClick={() => setEditing({ ...editing, ranges: [...editing.ranges, { from: editing.ranges[0]?.from || "", to: editing.ranges[0]?.to || "" }] })} className="text-[11px] text-primary hover:underline" data-testid="preset-form-add-range">
                  + {IT ? "Aggiungi" : "Add"}
                </button>
              </div>
              <div className="space-y-2">
                {editing.ranges.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-1.5" data-testid={`preset-form-range-${idx}`}>
                    <Input type="date" value={r.from} onChange={(e) => setEditing({ ...editing, ranges: editing.ranges.map((x, i) => i === idx ? { ...x, from: e.target.value } : x) })} className="flex-1"/>
                    <span className="text-muted-foreground text-xs">→</span>
                    <Input type="date" value={r.to} onChange={(e) => setEditing({ ...editing, ranges: editing.ranges.map((x, i) => i === idx ? { ...x, to: e.target.value } : x) })} className="flex-1"/>
                    {editing.ranges.length > 1 && (
                      <button type="button" onClick={() => setEditing({ ...editing, ranges: editing.ranges.filter((_, i) => i !== idx) })} className="text-destructive text-xs px-1" data-testid={`preset-form-remove-range-${idx}`}>×</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between gap-2 pt-3 border-t border-border">
              <Button variant="outline" onClick={() => setEditing(null)}>{IT ? "Annulla" : "Cancel"}</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="preset-form-save">
                {isNew ? (IT ? "Crea preset" : "Create preset") : (IT ? "Salva modifiche" : "Save changes")}
              </Button>
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex justify-end pt-3 border-t border-border">
            <Button variant="outline" onClick={onClose}>{IT ? "Chiudi" : "Close"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------- iCal (OTA) event detail dialog — read-only ----------
const OTA_DEEP_LINKS = {
  "booking.com": {
    label: "Booking Extranet",
    url: "https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/booking.html",
  },
  bookingcom: {
    label: "Booking Extranet",
    url: "https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/booking.html",
  },
  booking: {
    label: "Booking Extranet",
    url: "https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/booking.html",
  },
  airbnb: {
    label: "Airbnb Host",
    url: "https://www.airbnb.com/hosting/reservations",
  },
  "airbnb.com": {
    label: "Airbnb Host",
    url: "https://www.airbnb.com/hosting/reservations",
  },
  vrbo: {
    label: "Vrbo Owner Portal",
    url: "https://www.vrbo.com/traveler/th/inbox",
  },
  expedia: {
    label: "Expedia Partner Central",
    url: "https://apps.expediapartnercentral.com/",
  },
  "hotels.com": {
    label: "Expedia Partner Central",
    url: "https://apps.expediapartnercentral.com/",
  },
  agoda: {
    label: "Agoda YCS",
    url: "https://ycs.agoda.com/mldc/en-us/Reservation",
  },
  "google.com": {
    label: "Google Hotel Center",
    url: "https://www.google.com/hotels/manage",
  },
};

function resolveOtaDeepLink(source, extracted) {
  if (!source) return null;
  const src = String(source).toLowerCase();
  // Prefer the reservation URL captured directly from the DESCRIPTION when present
  if (extracted?.reservation_url) {
    for (const key of Object.keys(OTA_DEEP_LINKS)) {
      if (src.includes(key)) {
        return { ...OTA_DEEP_LINKS[key], url: extracted.reservation_url };
      }
    }
    return { label: "Apri prenotazione", url: extracted.reservation_url };
  }
  for (const key of Object.keys(OTA_DEEP_LINKS)) {
    if (src.includes(key)) return OTA_DEEP_LINKS[key];
  }
  return null;
}

function IcalEventDialog({ eventId, onClose }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const navigate = useNavigate();
  const open = !!eventId;
  const [loading, setLoading] = useState(false);
  const [ev, setEv] = useState(null);

  useEffect(() => {
    if (!open) { setEv(null); return; }
    let alive = true;
    setLoading(true);
    api.get(`/ical-events/${eventId}`)
      .then((r) => { if (alive) setEv(r.data); })
      .catch((e) => { toast.error(errMsg(e, IT ? "Errore" : "Error")); onClose(); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  if (!open) return null;
  const x = ev?.extracted || {};
  const ov = ev?.manual_overrides || {};
  // Effective guests: manual override wins over the feed-parsed value —
  // same rule the Prenotazioni + Reception surfaces apply so all three
  // views agree on the number.
  const guestsEff = (ov.guests != null && ov.guests > 0) ? ov.guests : x.guests;
  const meta = [
    ["guest_name", IT ? "Ospite" : "Guest", x.guest_name],
    ["confirmation_code", IT ? "Conferma" : "Confirmation", x.confirmation_code],
    ["booking_reference", IT ? "Rif. prenotazione" : "Booking ref.", x.booking_reference],
    ["email", "Email", x.email],
    ["phone", IT ? "Telefono" : "Phone", x.phone || (x.phone_last4 && `••••${x.phone_last4}`)],
    ["guests", IT ? "Ospiti" : "Guests", guestsEff],
    ["total_amount", IT ? "Totale" : "Total", x.total_amount],
  ].filter((m) => m[2] != null && m[2] !== "");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`ical-detail-dialog-${eventId}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {IT ? "Dettaglio prenotazione OTA" : "OTA booking detail"}
          </DialogTitle>
          <DialogDescription>
            {IT
              ? "Prenotazione importata via iCal. Puoi correggere qualsiasi campo con il tasto Modifica — le modifiche restano anche dopo la prossima sincronizzazione."
              : "Booking imported via iCal. Use Edit to correct any field — changes persist through the next sync."}
          </DialogDescription>
        </DialogHeader>

        {loading || !ev ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-base">{ev.summary || "—"}</div>
              <span className="text-[11px] px-2 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary whitespace-nowrap uppercase" data-testid="ical-source-pill">
                {ev.source}
              </span>
            </div>

            {(() => {
              const deep = resolveOtaDeepLink(ev.source, ev.extracted);
              if (!deep) return null;
              return (
                <a
                  href={deep.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-primary/40 bg-primary/5 hover:bg-primary hover:text-white transition-colors group"
                  data-testid="ota-deep-link"
                >
                  <div>
                    <div className="uppercase tracking-widest text-[10px] font-semibold text-primary group-hover:text-white">
                      {IT ? "Apri sul portale OTA" : "Open on OTA portal"}
                    </div>
                    <div className="text-sm font-semibold">{deep.label}</div>
                  </div>
                  <span className="text-lg">↗</span>
                </a>
              );
            })()}
            <div className="grid grid-cols-2 gap-3 border border-border rounded-md p-3 bg-sand/40">
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Camera" : "Room"}</div>
                <div>{ev.room_name || "—"}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">UID</div>
                <div className="font-mono text-xs truncate">{ev.uid}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">Check-in</div>
                <div>{(ev.start || "").slice(0, 10)}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">Check-out</div>
                <div>{(ev.end || "").slice(0, 10)}</div>
              </div>
              {ev.location && (
                <div className="col-span-2">
                  <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Posizione" : "Location"}</div>
                  <div>{ev.location}</div>
                </div>
              )}
              {ev.organizer && (
                <div className="col-span-2">
                  <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Organizzatore" : "Organizer"}</div>
                  <div className="truncate">{ev.organizer}</div>
                </div>
              )}
              {ev.attendees && ev.attendees.length > 0 && (
                <div className="col-span-2">
                  <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Partecipanti" : "Attendees"}</div>
                  <div className="truncate">{ev.attendees.join(", ")}</div>
                </div>
              )}
              {ev.categories && (
                <div className="col-span-2">
                  <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Categorie" : "Categories"}</div>
                  <div>{ev.categories}</div>
                </div>
              )}
            </div>

            {meta.length > 0 && (
              <div className="border border-sage/30 rounded-md p-3 bg-sage/5" data-testid="ical-extracted-fields">
                <div className="uppercase tracking-widest text-[10px] text-sage font-semibold mb-2">
                  {IT ? "Dati estratti dal feed" : "Fields extracted from the feed"}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {meta.map(([key, label, value]) => (
                    <div key={key} data-testid={`ical-extracted-${key}`}>
                      <div className="uppercase tracking-widest text-[9px] text-muted-foreground">{label}</div>
                      <div className="text-sm">{String(value)}</div>
                    </div>
                  ))}
                </div>
                {x.reservation_url && (
                  <a href={x.reservation_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-primary underline hover:no-underline" data-testid="ical-reservation-url">
                    {IT ? "Apri prenotazione sul portale" : "Open reservation on portal"} ↗
                  </a>
                )}
                {x.notes && (
                  <div className="mt-2 pt-2 border-t border-sage/20">
                    <div className="uppercase tracking-widest text-[9px] text-muted-foreground mb-0.5">{IT ? "Note" : "Notes"}</div>
                    <div className="text-xs italic whitespace-pre-line">{x.notes}</div>
                  </div>
                )}
              </div>
            )}

            {ev.description && (
              <details className="border border-border rounded-md">
                <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:bg-sand/40">
                  {IT ? "Testo grezzo del feed (DESCRIPTION)" : "Raw feed text (DESCRIPTION)"}
                </summary>
                <pre className="px-3 py-2 text-[11px] font-mono whitespace-pre-wrap bg-muted/30 border-t border-border max-h-40 overflow-y-auto" data-testid="ical-raw-description">{ev.description}</pre>
              </details>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} data-testid="ical-detail-close">
                {IT ? "Chiudi" : "Close"}
              </Button>
              <Button
                onClick={() => {
                  // Close the preview + jump to Prenotazioni with a deep link
                  // so BookingsTab auto-opens the full OTA edit modal
                  // (all fields unlocked, manual_overrides persisted server-side).
                  onClose();
                  navigate(`/dashboard?tab=bookings&booking=ota-${eventId}`);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 lift gap-1.5"
                data-testid="ical-detail-edit-btn"
              >
                {IT ? "Modifica prenotazione" : "Edit booking"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

